import { ulid } from 'ulid';
import _ from 'lodash';

export enum Gravity {
  // 水平方向
  LEFT = 3,             // 左对齐
  RIGHT = 5,            // 右对齐
  END = 8388613,        // 末端对齐（RTL 布局）
  START = 8388608,      // 起始对齐（RTL 布局）

  // 垂直方向
  TOP = 48,             // 上对齐
  BOTTOM = 80,          // 下对齐

  // 组合方向
  CENTER = 17,                   // 水平竖直都居中
  CENTER_HORIZONTAL = 1,         // 仅水平居中
  CENTER_VERTICAL = 16,          // 仅竖直居中

  // 位置常量
  NO_GRAVITY = 0,      // 无对齐
}

// ============ Gravity 映射 ============

/**
 * 获取所有可用的 Gravity 选项
 * 用于生成 Select 下拉框的选项
 * @param t 国际化翻译函数,用于获取 Gravity 的显示文本
 * @returns Gravity 选项数组,包含 value 和 label
 */
export function getGravityOptions(t?: (key: string) => string): Array<{ value: Gravity; label: string }> {
  const gravityKeys: Gravity[] = [
    Gravity.LEFT,
    Gravity.RIGHT,
    Gravity.START,
    Gravity.END,
    Gravity.TOP,
    Gravity.BOTTOM,
    Gravity.CENTER,
    Gravity.CENTER_HORIZONTAL,
    Gravity.CENTER_VERTICAL,
    Gravity.NO_GRAVITY,
  ];

  return gravityKeys.map(gravity => ({
    value: gravity,
    label: t ? t(`content.Gravity.${Gravity[gravity]}`) : Gravity[gravity],
  }));
}

export enum FloatShowPattern {
  // 仅在当前Activity显示
  CURRENT_ACTIVITY = 'CURRENT_ACTIVITY',
  // 一直显示
  ALL_TIME = 'ALL_TIME',
  // 仅前台显示
  FOREGROUND = 'FOREGROUND',
}

export enum FloatSidePattern {
  // 默认不贴边，跟随手指移动
  DEFAULT = 'DEFAULT',
  // 左、右、上、下四个方向固定（一直吸附在该方向边缘，只能在该方向的边缘移动）
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
  // 根据手指到屏幕边缘的距离，自动选择水平方向的贴边、垂直方向的贴边、四方向的贴边
  AUTO_HORIZONTAL = 'AUTO_HORIZONTAL',
  AUTO_VERTICAL = 'AUTO_VERTICAL',
  AUTO_SIDE = 'AUTO_SIDE',
  // 拖拽时跟随手指移动，结束时贴边
  RESULT_LEFT = 'RESULT_LEFT',
  RESULT_RIGHT = 'RESULT_RIGHT',
  RESULT_TOP = 'RESULT_TOP',
  RESULT_BOTTOM = 'RESULT_BOTTOM',
  RESULT_HORIZONTAL = 'RESULT_HORIZONTAL',
  RESULT_VERTICAL = 'RESULT_VERTICAL',
  RESULT_SIDE = 'RESULT_SIDE',
}
export interface WebViewJsParams {
  // -200 自动匹配屏幕宽度。0 自动匹配宽度。大于0就使用这个值来设置宽度
  fitContentWidth?: number;

  // 参看 fitContentWidth
  fitContentHeight?: number;

  // webview向js传递的数据，js可以主动调用webview的方法获取
  javaToJsDataString?: string;

  // 如果要匹配内容宽高的话，webview注入的js的id选择器
  fitContentId?: string;

  // 如果要匹配内容宽高的话，webview注入的js的selector选择器
  fitContentClass?: string;

  // 如果要匹配内容宽度的话，webview注入的js脚本，但是这个还没考虑好是否安全。
  fitContentJs?: string;
}

// webview的配置
export interface WebViewConf {
  // webview的背景颜色。html透明时用得到。
  backgroundColor?: number;

  // 为了防止跟系统冲突，-100是适用窗口宽高
  // 后端代码多了
  //   companion object{
  //         val fitScreenSize = -100
  //     }
  // 但是这个属性考虑要废弃
  //   layoutWidth?: number
  //   layoutHeight?: number

  // 对应 ViewGroup.LayoutParams
    // layoutParams?: Record<string, any>

  // 加载时的特定 user agent
  ua?: string;
}

export interface HtmlConf {
  // 如果是 url 格式，则用 url
  url?: string | null;

  // 如果是 html 格式则用 html 格式。
  // url 和 html 必须二选一
  html?: string | null;

  // 被注入js的名字。不设置就会会被注入一个统一的 Android
  jsBridgeName?: string;
  // webview的js配置
  webViewJsParams?: WebViewJsParams;
  //webview的配置
  webViewConf?: WebViewConf;

  // webViewJsName 默认值为 "AutoWebViewJs"
  // 注入的webViewJs
  // 现在可用的是下面几个
  // WebViewJs 普通的js调用
  // AutoWebViewJs  可以调用安卓解析js脚本
  // 对应安卓的
  // C:\work\android\libs\fanfanlo\lib\src\main\java\com\fanfanlo\lib\components\webview\WebViewJs.kt
  // C:\work\android\libs\fanfanlo\droid-lib\src\main\java\com\fanfanlo\droidlib\auto\components\webview\AutoWebViewJs.kt
  webViewJsName?: string;

  javaRunnerId?: number;

  /**
   jsBridgeName 为Android，则调用 Android.onJsCallFun("extraConf", "");时，将会返回extra的内容，安卓端是这样的 gson2.toJson(htmlConf.extra)。
   其中 extraConf 是固定的调用名称。
   
  */
 // 向html传递的额外配置，由被打开的html主动调用，${jsBridgeName}.onJsCallFun("extraConf","")来获取。
  extra?: Record<any, any>;
  // {
  //     // javaRunnerId:javaRunner.id,
  // }
}

export interface GravityInfo {
  // C:\work\android\libs\fanfanlo\lib\src\main\java\com\fanfanlo\lib\components\floatview\floatwindow\FloatWindow.kt
  // setGravity 里使用的
  gravity: Gravity;
  offsetX?: number;
  offsetY?: number;
}

// 边框设置
export interface BorderInfo {
  // 左
  left: number;
  //上
  top: number;
  // 右
  right: number;
  //下
  bottom: number;
}

//布局配置
export interface MatchParentInfo {
  //匹配父级宽度
  widthMatch: boolean;
  // 匹配父级高度
  heightMatch: boolean;
}

// 悬浮窗配置
export interface FloatConf {
  /** 位置保存名称，例如 "${positionSaveName}" */
  positionSaveName?: string;

  /** 是否可拖拽 */
  draggable?: boolean;

  /** 默认 X 坐标 */
  defaultX?: number; // 对应 Int，使用 number 表示整数

  /** 默认 Y 坐标 */
  defaultY?: number; // 对应 Int，使用 number 表示整数

  /** 重置 X 坐标，可选 */
  resetX?: number; // 对应 Int?，使用 number | null 表示可空整数

  /** 重置 Y 坐标，可选 */
  resetY?: number; // 对应 Int?，使用 number | null 表示可空整数

  /** 标签，可选 */
  tag?: string;
  
 /** 悬浮窗贴边模式 */
  sidePattern?: FloatSidePattern; // default is DEFAULT

  /** 浮窗显示模式 */
  showPattern?: FloatShowPattern;

  /** 是否包含 EditText */
  hasEditText?: boolean;

  /** 浮窗对齐方式和坐标偏移 */
  gravity?: GravityInfo;

  /** 布局大小变化后的对齐方式
   * html内容变化后，如果宽高变化了，导致webview重新度量，悬浮窗也会重新计算宽高和应该在的位置。如果要居中的话就要“竖直对齐”，如果要左上角对齐，则选择起始对齐。
   */
  
  layoutChangedGravity?: Gravity;

  /** 拖拽边界值 (left, top, right, bottom) */
  border?: BorderInfo;

  /** 宽高是否充满父布局 */
  matchParent?: MatchParentInfo;
}

// 悬浮窗窗口配置
export interface FloatWindowConf {
  // 悬浮窗配置
  floatConf?: FloatConf;

  // 前台服务。 如果有值就会有个前台服务启动，关闭浮窗后会同时关闭前台服务，前台服务点击后会关闭这个浮窗。这个功能可以在悬浮窗占空间过大，覆盖了关闭悬浮窗的按钮时使用前台服务来关闭悬浮窗。
  foregroundText?: string;
}

// 内容类型枚举
export enum ContentType {
  // 使用html
  Html = 'html',
  // 使用安卓的view。但是现在并没提供。
  View = 'view',
}

// 窗口类型枚举
export enum WindowType {
  // 悬浮窗。像小工具、闹钟类的可以使用
  Float = 'float',
  // 系统。系统级别窗口
  System = 'system',
  // 弹窗。一般用于对话框
  Dialog = 'dialog',
}
// 对话框窗口配置
export interface DialogWindowConf {
  // view的布局
  viewLayoutParams?: Record<string, any>;
  // 使用的主题
  theme?: string;
}
// 系统窗口配置。
export interface SysWindowConf {
  // view的布局
  viewLayoutParams?: Record<string, any>;
  // 是否拦截返回键
  listenBackKey: boolean;
}
// 窗口配置
export interface WindowConf {
  // 用于用户识别。
  tag?: string;
  // 窗口说明
  description?: string;
  // 这个属性不必显示给用户，因为现在只有html的选项。
  contentType: ContentType;
  // contentType现在只有 html一个选项，所以这个是必选
  htmlConf?: HtmlConf;

  windowType: WindowType;
  // 根据 windowType选择，下面三选一
  // 对话框配置
  dialogWindowConf?: DialogWindowConf;
  // 悬浮窗配置
  floatWindowConf?: FloatWindowConf;
  // 系统窗口配置
  systemWindowConf?: SysWindowConf;
  // 可以用ulid或者uuid
  windowId: string;
  // 强制关闭窗口的时间，默认为0，大于0会进行倒计时进行强行关闭。
  forceDismissWindowTime: number;
}

export function getDefaulWindowConf(): WindowConf {
  return JSON.parse(JSON.stringify(createDefaultWindowConf()));
}

export function createWindowConfCopy(conf: any) {
  return _.merge({}, createDefaultWindowConf(), conf);
}

// ============ createDefault 方法 ============

export function createDefaultWebViewJsParams(): WebViewJsParams {
  return {
    fitContentWidth: 0,
    fitContentHeight: 0,
    javaToJsDataString: ``,
  };
}

export function createDefaultWebViewConf(): WebViewConf {
  return {
    backgroundColor: 0,
  };
}

export function createDefaultGravityInfo(): GravityInfo {
  return {
    gravity: Gravity.START,
  };
}

export function createDefaultBorderInfo(): BorderInfo {
  return {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  };
}

export function createDefaultMatchParentInfo(): MatchParentInfo {
  return {
    widthMatch: false,
    heightMatch: false,
  };
}

export function createDefaultHtmlConf(): HtmlConf {
  return {
    url: ``,
    webViewJsParams: createDefaultWebViewJsParams(),
    webViewConf: createDefaultWebViewConf(),
    jsBridgeName: 'android',
    webViewJsName: 'AutoWebViewJs',
  };
}

export function createDefaultFloatConf(): FloatConf {
  return {
    positionSaveName: '${positionSaveName}',
    draggable: true,
    showPattern: FloatShowPattern.ALL_TIME,
    hasEditText: true,
    sidePattern: FloatSidePattern.DEFAULT,
    gravity: createDefaultGravityInfo(),
    layoutChangedGravity: Gravity.START,
    border: createDefaultBorderInfo(),
    matchParent: createDefaultMatchParentInfo(),
  };
}

export function createDefaultFloatWindowConf(): FloatWindowConf {
  return {
    floatConf: createDefaultFloatConf(),
  };
}

export function createDefaultDialogWindowConf(): DialogWindowConf {
  return {
    viewLayoutParams: {},
    theme: '',
  };
}

export function createDefaultSysWindowConf(): SysWindowConf {
  return {
    viewLayoutParams: {},
    listenBackKey: false,
  };
}

export function createDefaultWindowConf(): WindowConf {
  return {
    tag: '',
    contentType: ContentType.Html,
    windowType: WindowType.Float,
    htmlConf: createDefaultHtmlConf(),
    floatWindowConf: createDefaultFloatWindowConf(),
    windowId: ulid(),
    forceDismissWindowTime: 0,
  };
}



export function getWindowDismissEventType(id:string): String {
  return `window-${id}-dismiss`;
}
export function getWindowOpenedEventType(id:string): String {
  return `window-${id}-opened`;
}
export function getWindowHideEventType(id:string): String {
  return `window-${id}-hide`;
}